// Shared inline-style builders — ported from the mockup helpers (chipS / bannerS / mTh /
// mTd / mono / badge). The mockup styles everything inline; we keep that approach for a
// faithful pixel reproduction, centralising the repeated builders here.

import type { CSSProperties } from 'react';
import { C, hexA, MONO } from './tokens';

/** Status pill: glyph + text + color, translucent fill + border. */
export function chipS(color: string): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10.5,
    fontWeight: 600,
    color,
    background: hexA(color, 0.11),
    border: '1px solid ' + hexA(color, 0.35),
    fontFamily: MONO,
    whiteSpace: 'nowrap',
  };
}

/** Connection-banner row. */
export function bannerS(color: string): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    fontSize: 12,
    color,
    background: hexA(color, 0.1),
    borderBottom: '1px solid ' + hexA(color, 0.3),
  };
}

export function bannerBtnS(color: string): CSSProperties {
  return {
    background: 'none',
    border: '1px solid ' + hexA(color, 0.5),
    borderRadius: 5,
    color,
    fontSize: 11,
    padding: '3px 10px',
    fontFamily: 'inherit',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

/** Uppercase pill badge (alert state). */
export function badgeS(color: string): CSSProperties {
  return {
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: 4,
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    color,
    background: hexA(color, 0.12),
    border: '1px solid ' + hexA(color, 0.4),
  };
}

/** Mini-table header cell. */
export function mTh(align: 'left' | 'right' = 'left'): CSSProperties {
  return {
    padding: '6px 12px',
    fontSize: 10,
    color: C.dm,
    fontWeight: 600,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    textAlign: align,
    borderBottom: '1px solid ' + C.ln2,
    whiteSpace: 'nowrap',
  };
}

/** Mini-table body cell. */
export function mTd(align: 'left' | 'right' = 'left', extra?: CSSProperties): CSSProperties {
  return {
    padding: '6px 12px',
    fontSize: 11.5,
    textAlign: align,
    borderBottom: '1px solid #16203A',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    ...extra,
  };
}

export function mono(color?: string): CSSProperties {
  return { fontFamily: MONO, color: color ?? C.tx };
}

export const CARD: CSSProperties = {
  background: C.s1,
  border: '1px solid ' + C.ln,
  borderRadius: 8,
};

export const CARD_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  borderBottom: '1px solid ' + C.ln2,
};

export const H2: CSSProperties = { margin: 0, fontSize: 12.5, fontWeight: 600 };

export const PBTN: CSSProperties = {
  background: C.bl,
  border: 'none',
  borderRadius: 6,
  color: '#0B1220',
  fontWeight: 600,
  fontSize: 12.5,
  padding: '8px 14px',
  fontFamily: 'inherit',
};

export const GBTN: CSSProperties = {
  background: 'none',
  border: '1px solid ' + C.ln,
  borderRadius: 6,
  color: C.b9,
  fontSize: 11.5,
  padding: '7px 12px',
  fontFamily: 'inherit',
  textAlign: 'left',
};

export const GBTN_SM: CSSProperties = {
  background: 'none',
  border: '1px solid ' + C.ln,
  borderRadius: 5,
  color: C.b9,
  fontSize: 10.5,
  padding: '3px 9px',
  fontFamily: 'inherit',
  fontWeight: 600,
};

export const CHIP_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 5,
  fontSize: 11.5,
  border: '1px solid ' + C.ln,
  background: '#121B2C',
  color: C.b9,
  fontFamily: 'inherit',
};

export const CHIP_ON: CSSProperties = {
  ...CHIP_BASE,
  border: '1px solid rgba(76,158,232,.6)',
  background: 'rgba(76,158,232,.14)',
  color: '#CFE4F7',
};

/** Toggle-chip style helper (on/off). */
export function chipToggle(on: boolean, extra?: CSSProperties): CSSProperties {
  return { ...(on ? CHIP_ON : CHIP_BASE), ...extra };
}
